import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/authStore";
import { TEST_IDS } from "../constants/testIds";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";
import { PasswordStrength, isPasswordValid } from "../components/auth/PasswordStrength";
import { SerpixWordmark } from "../components/brand/SerpixWordmark";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { COMMON_TIMEZONES } from "../lib/dates";

export default function Register() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const register = useAuthStore((s) => s.register);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    tenant_name: "",
    timezone: "Europe/Istanbul",
  });
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const pwValid = isPasswordValid(form.password);
  const pwMatch = confirm.length > 0 && form.password === confirm;
  const showMismatch = confirm.length > 0 && !pwMatch;
  const canSubmit = pwValid && pwMatch && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwValid) {
      setError(t("auth.pw_invalid_hint"));
      return;
    }
    if (!pwMatch) {
      setError(t("auth.pw_mismatch"));
      return;
    }
    setLoading(true);
    try {
      await register(form);
      nav("/app/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden bg-zinc-100">
        <img
          src="https://images.pexels.com/photos/1029622/pexels-photo-1029622.jpeg"
          alt=""
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 inline-flex items-center gap-2">
            <SerpixWordmark size="sm" />
          </Link>
          <h1 className="mb-2 font-display text-3xl font-semibold text-zinc-950">
            {t("auth.register")}
          </h1>
          <p className="mb-8 text-sm text-zinc-500">
            {i18n.language === "en"
              ? "14-day free trial · $19/mo"
              : "14 gün ücretsiz deneme · $19/ay"}
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.name")}
              </Label>
              <Input
                required
                value={form.name}
                onChange={setField("name")}
                data-testid={TEST_IDS.registerName}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.tenant_name")}
              </Label>
              <Input
                required
                value={form.tenant_name}
                onChange={setField("tenant_name")}
                data-testid={TEST_IDS.registerTenantName}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.email")}
              </Label>
              <Input
                type="email"
                required
                autoComplete="username"
                value={form.email}
                onChange={setField("email")}
                data-testid={TEST_IDS.registerEmail}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.password")}
              </Label>
              <PasswordInput
                required
                minLength={8}
                autoComplete="new-password"
                value={form.password}
                onChange={setField("password")}
                data-testid={TEST_IDS.registerPassword}
                testIdToggle="register-password-toggle"
                className="mt-1"
              />
              <PasswordStrength password={form.password} hideWhenEmpty />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.confirm_password_label")}
              </Label>
              <PasswordInput
                required
                minLength={8}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="register-password-confirm-input"
                testIdToggle="register-password-confirm-toggle"
                className="mt-1"
                aria-invalid={showMismatch ? "true" : "false"}
              />
              {showMismatch && (
                <p
                  className="mt-1 text-xs font-medium text-red-600"
                  data-testid="register-password-mismatch"
                >
                  {t("auth.pw_mismatch")}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.timezone")}
              </Label>
              <Select
                value={form.timezone}
                onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}
              >
                <SelectTrigger
                  className="mt-1"
                  data-testid={TEST_IDS.registerTimezone}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid={TEST_IDS.registerSubmit}
              className="w-full bg-gradient-to-r from-[#5B4CFB] to-[#7c5cf6] text-white shadow-lg shadow-violet-500/25 hover:from-[#4f40f0] hover:to-[#6f4df0]"
            >
              {loading ? "…" : t("auth.register")}
            </Button>
          </form>

          <div className="mt-6 text-xs text-zinc-500">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-zinc-900 underline underline-offset-4">
              {t("auth.login")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
